import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Card,
  Form,
  Input,
  DatePicker,
  Button,
  Table,
  Select,
  Space,
  message,
  Spin,
  Typography,
  Row,
  Col,
  Tag,
  Divider,
  Alert,
  Tooltip
} from 'antd';

import {
  SearchOutlined,
  DownloadOutlined,
  DeleteOutlined,
  ClearOutlined,
  ReloadOutlined,
  FilterOutlined,
  ClockCircleOutlined,
  DownOutlined, 
  RightOutlined,
  SettingOutlined,
  RocketOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';

import FlightLifecycle from './FlightLifecycle';

import dayjs from 'dayjs';

// Add CSS styles
const styles = `
  .mismatch-row {
    background-color: rgba(255, 0, 0, 0.1) !important;
  }
  
  .mismatch-cell {
    color: #ff4d4f !important;
    font-weight: 500;
  }
  
  .event-badge {
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .event-flight_departure {
    background-color: #e6f7ff;
    color: #1890ff;
    border: 1px solid #91d5ff;
  }
  
  .event-gate_change {
    background-color: #fff2e8;
    color: #fa8c16;
    border: 1px solid #ffd591;
  }
  
  .event-delay {
    background-color: #fff1f0;
    color: #f5222d;
    border: 1px solid #ffa39e;
  }
  
  .event-boarding {
    background-color: #f6ffed;
    color: #52c41a;
    border: 1px solid #b7eb8f;
  }
  
  .event-baggage_claim {
    background-color: #f9f0ff;
    color: #722ed1;
    border: 1px solid #d3adf7;
  }
  
  .event-default {
    background-color: #f0f0f0;
    color: #666;
    border: 1px solid #d9d9d9;
  }
  
  .ant-table-tbody > tr.mismatch-row {
    background-color: rgba(255, 235, 235, 0.3) !important;
  }
  
  .ant-table-tbody > tr.mismatch-row:hover {
    background-color: rgba(255, 235, 235, 0.5) !important;
  }
  
  .sts-highlight-row {
    background-color: rgba(24, 144, 255, 0.1) !important;
    border-left: 4px solid #1890ff !important;
    font-weight: 500;
  }
  
  .sts-history-row {
    background-color: rgba(24, 144, 255, 0.05) !important;
    border-left: 4px solid #40a9ff !important;
  }
  
  .sts-highlight-row:hover {
    background-color: rgba(24, 144, 255, 0.15) !important;
  }
`;


// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;

const API_KEY_RETRIEVE = "rcLFWkIlDH9dPlQLNAP7p4wlymHpipar6nAfhSuQ";
const API_KEY_RULE = "9LkCMxIqbU6T32baTiodW8Kga9ocC3WG37Nvb7m0";

const FIELD_PRIORITY = {
  departure: 1,
  arrival: 2,
  gate: 3,
  terminal: 4,
  baggage: 5,
  misc: 99,
};

const UPDATED_TEXT_FIELD_MAP = {
  STS: "The status of the flight",
  DGT: "The departure gate",
  DTM: "The departure terminal",
  AGT: "The arrival gate",
  ATM: "The arrival terminal",
  DIV: "The airport for a diversion",
  BGG: "The baggage claim information",
  TAL: "The tail number of the flight",
  SQP: "The scheduled IATA aircraft type code",
  AQP: "The actual IATA aircraft type code",
};

const UPDATED_DATE_FIELD_MAP = {
  SGD: "The scheduled gate departure",
  EGD: "The estimated gate departure",
  AGD: "The actual gate departure",
  SGA: "The scheduled gate arrival",
  EGA: "The estimated gate arrival",
  AGA: "The actual gate arrival",
  SRD: "The scheduled runway departure",
  ERD: "The estimated runway departure",
  ARD: "The actual runway departure",
  SRA: "The scheduled runway arrival",
  ERA: "The estimated runway arrival",
  ARA: "The actual runway arrival",
};

const CiriumService = () => {
  // Form states
  const [form] = Form.useForm();
  const [flightNumber, setFlightNumber] = useState("");
  const [carrierCode, setCarrierCode] = useState("");
  const [departureDate, setDepartureDate] = useState(dayjs());
  const [departureAirportCode, setDepartureAirportCode] = useState("");

  // Data states
  const [flightDetails, setFlightDetails] = useState(null);
  const [rulesCreated, setRulesCreated] = useState({
    upcoming: { flights: [] },
    history: { flights: [] }
  });
  const [ruleResponse, setRuleResponse] = useState(null);

  // Loading states
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingRule, setLoadingRule] = useState(false);

  // Filter states
  const [selectedEventType, setSelectedEventType] = useState("ALL");
  const [timeFilter, setTimeFilter] = useState("ALL");
  const [updatedFieldTypeFilter, setUpdatedFieldTypeFilter] = useState("ALL");
  const [updatedEventTypeFilter, setUpdatedEventTypeFilter] = useState("ALL");
  const [statusHistoryEventFilter, setStatusHistoryEventFilter] = useState("ALL");
  const [statusHistoryFieldTypeFilter, setStatusHistoryFieldTypeFilter] = useState("ALL");

  // Change tracking states
  const [changedRows, setChangedRows] = useState([]);

  const [showAllHistory, setShowAllHistory] = useState(false);


  const saveRules = (rules) => {
    setRulesCreated(rules);
    localStorage.setItem("flightRules", JSON.stringify(rules));
  };

  // Add after other state declarations
  const [expandedFieldCodes, setExpandedFieldCodes] = useState({});

  const toggleFieldExpand = (fieldCode) => {
    setExpandedFieldCodes(prev => ({
      ...prev,
      [fieldCode]: !prev[fieldCode]
    }));
  };

  


  useEffect(() => {
    const savedRules = localStorage.getItem("flightRules");
    if (savedRules) {
      try {
        const parsed = JSON.parse(savedRules);
        if (parsed.upcoming && parsed.history) {
          setRulesCreated(parsed);
        }
      } catch (e) {
        console.error("Error parsing saved rules", e);
      }
    }
  }, []);

  const formatTimestamp = (timestamp) => {
    if (!timestamp || timestamp === "" || timestamp === "undefined") return "-";

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return timestamp;
      }

      const hasTimezone = timestamp.includes("+") || timestamp.includes("Z");

      if (hasTimezone) {
        const utcTime = date.toISOString().replace("T", " ").slice(0, -5) + " UTC";
        const localTimeStr = timestamp.split("+")[0].replace("T", " ").slice(0, -4);
        const istTime = localTimeStr + " IST";
        return `${utcTime} / ${istTime}`;
      } else {
        const utcTime = date.toISOString().replace("T", " ").slice(0, -5) + " UTC";
        const istDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
        const istTime = istDate.toISOString().replace("T", " ").slice(0, -5) + " IST";
        return `${utcTime} / ${istTime}`;
      }
    } catch (error) {
      return timestamp;
    }
  };

  const formatSimpleTimestamp = (timestamp) => {
    if (!timestamp || timestamp === "" || timestamp === "undefined") return "-";

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return timestamp;
      }

      const hasTimezone = timestamp.includes("+") || timestamp.includes("Z");

      if (hasTimezone) {
        const localTime = timestamp.split("+")[0].split("Z")[0];
        return localTime.replace("T", " ");
      } else {
        return timestamp.replace("T", " ").replace(/\.\d{3}$/, "");
      }
    } catch (error) {
      return timestamp;
    }
  };

  const formatToISTOnly = (value) => {
    if (!value) return '-';

    if (typeof value === 'string' && value.includes(' / ')) {
      const parts = value.split(' / ');
      return parts[1] || parts[0];
    }

    return formatSimpleTimestamp(value);
  };

  const isTimestampValue = (value, fieldName) => {
    if (!value || typeof value !== "string") return false;
    
    const timestampFieldPatterns = [
      "date", "time", "arrival", "departure", "gate", "runway",
      "estimated", "actual", "scheduled", "utc", "local",
    ];
    
    const fieldContainsTimeKeyword = timestampFieldPatterns.some((pattern) =>
      fieldName.toLowerCase().includes(pattern)
    );
    
    const isoTimestampPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
    const localTimestampPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?$/;
    
    return fieldContainsTimeKeyword && (isoTimestampPattern.test(value) || localTimestampPattern.test(value));
  };

  const formatValue = (value, fieldName) => {
    if (!value) return "-";
    
    if (isTimestampValue(value, fieldName)) {
      return formatSimpleTimestamp(value);
    }

    return value;
  };

  const handleGetFlightDetails = async (values) => {
    setLoadingDetails(true);
    setRuleResponse(null);
    setFlightDetails(null);
    setSelectedEventType("ALL");

    try {
      const url = "https://qrvs13q4xx.dev.digiyatrafoundation.org/v01-dev/retrieve-flight-details-alt";
      
      const params = {
        flightNumber: values?.flightNumber || flightNumber,
        carrierCode: values?.carrierCode || carrierCode,
        departureDate: values?.departureDate ? dayjs(values.departureDate).format('YYYY-MM-DD') : departureDate.format('YYYY-MM-DD'),
        departureAirportCode: values?.departureAirportCode || departureAirportCode,
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": API_KEY_RETRIEVE,
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const responseJson = await response.json();
        throw new Error(`errorMsg: ${responseJson.message}`);
      }

      const data = await response.json();
      setFlightDetails(data);
      message.success('Flight details retrieved successfully!');

    } catch (error) {
      console.log("Error retrieving flight details:", error);
      const errMsg = error?.message?.replace(/^errorMsg:\s*/, '') || "Error retrieving flight details.";
      message.error(errMsg);
      setRuleResponse({ message: errMsg });
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCreateFlightRule = async () => {
    if (!flightNumber || !carrierCode || !departureAirportCode) {
      message.warning('Please fill all required fields');
      return;
    }

    setLoadingRule(true);
    try {
      const url = "https://qrvs13q4xx.dev.digiyatrafoundation.org/v01-dev/flight-rule-creation-alt";
      
      const params = [{
        flightNumber,
        carrierCode,
        departureDate: departureDate.format('YYYY-MM-DD'),
        departureAirport: departureAirportCode,
      }];

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": API_KEY_RULE,
        },
        body: JSON.stringify(params),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.success === false) {
        const msg = data?.message || data?.error || `Rule creation failed (status ${response.status})`;
        console.error("Rule creation failed:", msg);
        message.error(msg);
        setRuleResponse({ message: msg });
        return;
      }

      setRuleResponse(data);
      setFlightDetails(null);

      const flightId = `${departureAirportCode}-${carrierCode}-${flightNumber}-${departureDate.format("YYYY-MM-DD")}`;

      setRulesCreated((prev) => {
        const base = prev && prev.upcoming && prev.history ? prev : { upcoming: { flights: [] }, history: { flights: [] } };

        const alreadyExists = base.upcoming.flights.includes(flightId) || base.history.flights.includes(flightId);

        if (alreadyExists) return base;

        const updated = {
          ...base,
          upcoming: {
            flights: [...base.upcoming.flights, flightId],
            count: (base.upcoming.count || base.upcoming.flights.length) + 1,
          },
        };

        localStorage.setItem("flightRules", JSON.stringify(updated));
        return updated;
      });

      message.success(data?.message || "Flight rule created successfully!");

    } catch (error) {
      console.error("Error creating flight rule:", error);
      message.error('Error creating flight rule');
      setRuleResponse({ message: "Error creating flight rule." });
    } finally {
      setLoadingRule(false);
    }
  };

  useEffect(() => {
    const fetchRecords = async () => {
      try {
        const params = { "all": "true" };
        const response = await fetch("https://qrvs13q4xx.dev.digiyatrafoundation.org/v01-dev/retrieve-flight-details-alt", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": API_KEY_RETRIEVE,
          },
          body: JSON.stringify(params),
        });
        const data = await response.json();
        setRulesCreated(data);
        localStorage.setItem("flightRules", JSON.stringify(data));
      } catch (e) {
        console.error("Error fetching records:", e);
      }
    };

    fetchRecords();
  }, []);

  const populateFromRule = (rule) => {
    try {
      let cleanRule = rule;
      if (rule.includes("-(")) cleanRule = rule.split("-(")[0];
      else if (rule.includes("(")) cleanRule = rule.split("(")[0];

      const parts = cleanRule.split("-");
      
      setDepartureAirportCode(parts[0] || "");
      setCarrierCode(parts[1] || "");
      setFlightNumber(parts[2] || "");
      
      if (parts[3] && parts[4] && parts[5]) {
        const date = `${parts[3]}-${parts[4]}-${parts[5]}`;
        setDepartureDate(dayjs(date));
      }

      form.setFieldsValue({
        departureAirportCode: parts[0] || "",
        carrierCode: parts[1] || "",
        flightNumber: parts[2] || "",
        departureDate: parts[3] && parts[4] && parts[5] ? dayjs(`${parts[3]}-${parts[4]}-${parts[5]}`) : dayjs(),
      });

      message.success('Rule data populated successfully!');
    } catch (error) {
      console.error('Error populating from rule:', error);
      message.error('Error populating rule data');
    }
  };

  const deleteRule = (index) => {
    setRulesCreated((prev) => {
      const base = prev && prev.upcoming && prev.history ? prev : { upcoming: { flights: [] }, history: { flights: [] } };

      const newFlights = base.upcoming.flights.filter((_, i) => i !== index);

      const updated = {
        ...base,
        upcoming: {
          ...base.upcoming,
          flights: newFlights,
          count: newFlights.length,
        },
      };

      localStorage.setItem("flightRules", JSON.stringify(updated));
      return updated;
    });
    message.success("Rule deleted successfully!");
  };

  const clearAllRules = () => {
    const empty = {
      upcoming: { flights: [], count: 0 },
      history: { flights: [], count: 0 },
    };
    setRulesCreated(empty);
    localStorage.setItem("flightRules", JSON.stringify(empty));
    message.success("All rules cleared successfully!");
  };

  const downloadJSON = () => {
    if (!flightDetails) {
      message.warning('No flight details to download');
      return;
    }

    try {
      const blob = new Blob([JSON.stringify(flightDetails, null, 2)], {
        type: "application/json",
      });
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      link.href = URL.createObjectURL(blob);
      link.download = `flight-details-${flightNumber || "data"}-${timestamp}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('JSON file downloaded successfully!');
    } catch (error) {
      console.error('Error downloading JSON:', error);
      message.error('Error downloading JSON file');
    }
  };

  const downloadCSV = () => {
    if (!flightDetails) {
      message.warning('No flight details to download');
      return;
    }

    try {
      const allRows = buildTableRows(flightDetails);
      const headers = [
        "Field",
        "Change Timestamp",
        "Event Type",
        "Initial Value",
        "Final Value",
        "Value History",
      ];

      const csvContent = [
        headers.join(","),
        ...allRows.map((row) =>
          [
            `"${row.field}"`,
            `"${formatTimestamp(row.timestamp)}"`,
            `"${row.eventType || "-"}"`,
            `"${formatValue(row.initialValue, row.field)}"`,
            `"${formatValue(row.finalValue, row.field)}"`,
            `"${row.valueHistory.map((val) => formatValue(val, row.field)).join(", ")}"`,
          ].join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      link.href = URL.createObjectURL(blob);
      link.download = `flight-details-${flightNumber || "data"}-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('CSV file downloaded successfully!');
    } catch (error) {
      console.error('Error downloading CSV:', error);
      message.error('Error downloading CSV file');
    }
  };

  const downloadUpdatedCSV = () => {
    if (!updatedRows || updatedRows.length === 0) {
      message.warning('No updated fields to download');
      return;
    }

    try {
      const headers = [
        'Field Code',
        'Field Name',
        'Field Type',
        'Original Value',
        'New Value',
        'Event Type',
        'Update Timestamp',
      ];

      const csvContent = [
        headers.join(','),
        ...updatedRows.map((row) =>
          [
            `"${row.fieldCode || ''}"`,
            `"${row.description || ''}"`,
            `"${row.fieldType || ''}"`,
            `"${row.originalValue || ''}"`,
            `"${row.newValue || ''}"`,
            `"${row.eventType || ''}"`,
            `"${formatTimestamp(row.timestamp) || ''}"`,
          ].join(',')
        ),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      link.href = URL.createObjectURL(blob);
      link.download = `updated-fields-${flightNumber || 'data'}-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('Updated fields CSV downloaded successfully!');
    } catch (error) {
      console.error('Error downloading updated fields CSV:', error);
      message.error('Error downloading updated fields CSV');
    }
  };

  const downloadStatusHistoryCSV = () => {
    if (!statusHistoryRows || statusHistoryRows.length === 0) {
      message.warning('No status history to download');
      return;
    }

    try {
      const headers = [
        'Field',
        'From',
        'To',
        'Source',
        'Event Type',
        'Timestamp',
      ];

      const csvContent = [
        headers.join(','),
        ...statusHistoryRows.map((row) =>
          [
            `"${row.field || ''}"`,
            `"${row.from || ''}"`,
            `"${row.to || ''}"`,
            `"${row.source || ''}"`,
            `"${row.eventType || ''}"`,
            `"${formatTimestamp(row.timestamp) || ''}"`,
          ].join(',')
        ),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      link.href = URL.createObjectURL(blob);
      link.download = `status-history-${flightNumber || 'data'}-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('Status history CSV downloaded successfully!');
    } catch (error) {
      console.error('Error downloading status history CSV:', error);
      message.error('Error downloading status history CSV');
    }
  };

  const buildHistoryText = (fieldChanges) => {
  if (!fieldChanges || fieldChanges.length <= 1) return '';
  
  const sorted = fieldChanges.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const historyParts = sorted.map((change, idx) => {
    const ts = formatSimpleTimestamp(change.timestamp);
    return idx === 0 
      ? `${change.from || '-'} → ${change.to || '-'} [${ts}]`
      : `${change.from || '-'} → ${change.to || '-'} [${ts}]`;
  });
  
  return historyParts.join('\n');
};

  const buildTableRows = (details) => {
    const rows = [];

    Object.entries(details).forEach(([field, values]) => {
      // Skip eventHistory field
      if (field === 'eventHistory' || field === 'statusUpdateHistory') return;
      
      if (!Array.isArray(values) || values.length === 0) return;

      const valueHistory = [];
      values.forEach((entry) => {
        const val = entry.value !== undefined ? entry.value : entry.initialValue;
        if (val) valueHistory.push(val);
      });

      const trueInitialValue = values[0]?.initialValue || values[0]?.value || "";

      values.forEach((entry) => {
        const timestamp = entry.timestamp || "";
        const eventType = entry.eventType || "";
        const currentValue = entry.value !== undefined ? entry.value : entry.initialValue;
        const initVal = trueInitialValue;

        if (!(timestamp || initVal || currentValue)) return;

        let category = "misc";
        for (const key in FIELD_PRIORITY) {
          if (field.toLowerCase().includes(key)) {
            category = key;
            break;
          }
        }

        const hasMismatch = initVal !== currentValue;
        
        // Determine if this is a new field or updated field
        const isNewField = !hasMismatch && initVal !== "" && currentValue !== "";
        const fieldStatus = isNewField ? "new" : (hasMismatch ? "updated" : null);

        rows.push({
          key: `${field}-${timestamp}-${Math.random()}`,
          field,
          fieldStatus,
          category,
          timestamp,
          eventType,
          initialValue: initVal,
          finalValue: currentValue,
          valueHistory: valueHistory,
          hasMismatch: hasMismatch,
        });
      });
    });

    rows.sort((a, b) => {
      const categoryDiff = FIELD_PRIORITY[a.category] - FIELD_PRIORITY[b.category];
      if (categoryDiff !== 0) return categoryDiff;
      return new Date(b.timestamp) - new Date(a.timestamp);
    });

    return rows;
  };

    

  const getUniqueEventTypes = (details) => {
    if (!details || !details.eventHistory) return [];

    return details.eventHistory.map((event) => {
      const full = formatTimestamp(event.timestamp);
      const parts = full.split(" / ");
      const istPart = parts.length > 1 ? parts[1] : full;

      return {
        id: `${event.eventType}|${event.timestamp}`,
        label: `${event.eventType} (${istPart})`,
        eventType: event.eventType,
        timestamp: event.timestamp,
        dataSource: event.dataSource || "Unknown",
      };
    });
  };

  const normalizeTs = (ts) => {
  if (!ts) return '';
  return String(ts)
    .replace('Z', '')
    .replace(/\.\d+$/, ''); // drop millis
};

const filterRowsByEventType = (rows, eventTypeId) => {
  if (!flightDetails || !flightDetails.eventHistory || eventTypeId === 'ALL') {
    return rows;
  }

  const [eventType, ts] = eventTypeId.split('|');
  const targetTs = normalizeTs(ts || '');

  return rows.filter((row) => {
    const rowTs = normalizeTs(row.timestamp);
    const sameTimestamp = targetTs && rowTs === targetTs;

    const sameEventType =
      row.eventType &&
      String(row.eventType).toUpperCase() === String(eventType).toUpperCase();

    // keep row when timestamp matches OR (no timestamp match but event type matches)
    return sameTimestamp || (!targetTs && sameEventType);
  });
};

  // const filterRowsByTimeType = (rows) => {
  //   if (!rows || !Array.isArray(rows)) return [];

  //   return rows.filter((row) => {
  //     if (!row.field) return false;
  //     const fld = row.field.toLowerCase();
  //     const isLocal = fld.includes("local");
  //     const isUTC = fld.includes("utc");

  //     if (timeFilter === "ALL") return true;
  //     if (timeFilter === "LOCAL") return isLocal;
  //     if (timeFilter === "UTC") return isUTC;

  //     return true;
  //   });
  // };
  const buildFlightSummary = (details) => {
  if (!details) return [];

  const getLatestValue = (fieldPath) => {
    const values = details[fieldPath];
    if (!Array.isArray(values) || !values.length) return null;
    const latest = values.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
    return latest?.value ?? latest?.finalValue ?? latest?.initialValue;
  };

  return [{
    // Exact field mappings requested
    std: getLatestValue('operationalTimes.scheduledGateDeparture'),      // SGD
    etd: getLatestValue('operationalTimes.estimatedGateDeparture'),     // EGD  
    atd: getLatestValue('operationalTimes.actualGateDeparture'),        // AGD
    sta: getLatestValue('operationalTimes.scheduledGateArrival'),       // SGA
    eta: getLatestValue('operationalTimes.estimatedRunwayArrival'),     // ERA
    ata: getLatestValue('operationalTimes.actualRunwayArrival'),        // ARA
    
    // Origin/Destination
    origin: getLatestValue('departureAirportFsCode') || '-',
    dest: getLatestValue('arrivalAirportFsCode') || '-',
    
    // Terminals (DTM=Origin, ATM=Dest)
    originTerminal: getLatestValue('airportResources.departureTerminal') || '-',
    destTerminal: getLatestValue('airportResources.arrivalTerminal') || '-',
    
    // Status
    status: getLatestValue('status') || 'Unknown',

    
    
    // Other info
    tailNumber: getLatestValue('flightEquipment.tailNumber'),
    aircraft: getLatestValue('flightEquipment.actualEquipmentIataCode'),
    baggage: getLatestValue('airportResources.baggage')
  }];
};



  useEffect(() => {
    const computeChangesSummary = () => {
      try {
        if (!flightDetails) {
          setChangedRows([]);
          return;
        }

        const changeSummary = [];
        const processedFields = new Set();

        Object.entries(flightDetails).forEach(([field, values]) => {
          if (!Array.isArray(values) || values.length === 0) return;
          if (processedFields.has(field)) return;

          const sortedValues = [...values].sort((a, b) => 
            new Date(a.timestamp || 0) - new Date(b.timestamp || 0)
          );

          const firstEntry = sortedValues[0];
          const lastEntry = sortedValues[sortedValues.length - 1];

          const firstValue = firstEntry?.initialValue || firstEntry?.value || "";
          const lastValue = lastEntry?.value !== undefined ? lastEntry?.value : lastEntry?.initialValue || "";

          if (firstValue !== lastValue) {
            changeSummary.push({
              field: field,
              previousValue: String(firstValue),
              currentValue: String(lastValue),
              timestamp: lastEntry?.timestamp || "",
              fieldName: field,
            });
          }

          processedFields.add(field);
        });

        changeSummary.sort((a, b) => {
          let categoryA = "misc";
          let categoryB = "misc";
          
          for (const key in FIELD_PRIORITY) {
            if (a.field.toLowerCase().includes(key)) categoryA = key;
            if (b.field.toLowerCase().includes(key)) categoryB = key;
          }
          
          const priorityDiff = FIELD_PRIORITY[categoryA] - FIELD_PRIORITY[categoryB];
          if (priorityDiff !== 0) return priorityDiff;
          
          return a.field.localeCompare(b.field);
        });

        setChangedRows(changeSummary);
      } catch (error) {
        console.error("Error computing change summary:", error);
        setChangedRows([]);
      }
    };

    computeChangesSummary();
  }, [flightDetails]);

  // KEEP THIS FUNCTION INTACT - handles old format
  const buildUpdatedRows = (details) => {
    if (!details) return [];

    const rows = [];
    const processed = new Set();

    const pushRow = ({
      fieldCode,
      fieldType,
      originalValue,
      newValue,
      timestamp,
      eventType,
    }) => {
      if (!fieldCode) return;

      const uniqueKey = `${fieldType}-${fieldCode}-${eventType}-${timestamp}`;
      if (processed.has(uniqueKey)) return;
      processed.add(uniqueKey);

      const description =
        fieldType === "TEXT"
          ? UPDATED_TEXT_FIELD_MAP[fieldCode] || fieldCode
          : UPDATED_DATE_FIELD_MAP[fieldCode] || fieldCode;

      rows.push({
        key: uniqueKey,
        fieldCode,
        description,
        fieldType,
        originalValue,
        newValue,
        timestamp,
        eventType,
      });
    };

    

    Object.entries(details).forEach(([field, values]) => {
      if (!Array.isArray(values) || values.length === 0) return;

      // TEXT fields
      if (field.includes("Updatedtextfield") && field.endsWith("Field")) {
        const base = field.replace(/Field$/, "");
        const code = values[0]?.initialValue || values[0]?.value || "";

        const newTextField = details[`${base}Newtext`] || details[`${base}NewText`];
        const originalTextField = details[`${base}Originaltext`] || details[`${base}OriginalText`];

        const newText = newTextField?.[0]?.initialValue ?? newTextField?.[0]?.value ?? "";
        const originalText = originalTextField?.[0]?.initialValue ?? originalTextField?.[0]?.value ?? "";

        const latestEntry = [...values].sort(
          (a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0)
        )[values.length - 1];

        const ts = latestEntry?.timestamp || newTextField?.[0]?.timestamp || originalTextField?.[0]?.timestamp || "";
        const evt = latestEntry?.eventType || "";

        pushRow({
          fieldCode: code,
          fieldType: "TEXT",
          originalValue: originalText,
          newValue: newText,
          timestamp: ts,
          eventType: evt,
        });
      }

      // DATE fields
      if (field.includes("Updateddatefield") && field.endsWith("Field")) {
        const base = field.replace(/Field$/, "");
        const code = values[0]?.initialValue || values[0]?.value || "";

        const newUtcField = details[`${base}Newdateutc`] || details[`${base}NewdateUTC`];
        const newLocalField = details[`${base}Newdatelocal`] || details[`${base}NewdateLocal`];
        const origUtcField = details[`${base}Originaldateutc`] || details[`${base}OriginaldateUTC`];
        const origLocalField = details[`${base}Originaldatelocal`] || details[`${base}OriginaldateLocal`];

        const newUtc = newUtcField?.[0]?.initialValue ?? newUtcField?.[0]?.value ?? "";
        const newLocal = newLocalField?.[0]?.initialValue ?? newLocalField?.[0]?.value ?? "";
        const origUtc = origUtcField?.[0]?.initialValue ?? origUtcField?.[0]?.value ?? "";
        const origLocal = origLocalField?.[0]?.initialValue ?? origLocalField?.[0]?.value ?? "";

        const newCombined = newUtc && newLocal
          ? `${formatSimpleTimestamp(newUtc)} / ${formatSimpleTimestamp(newLocal)}`
          : formatSimpleTimestamp(newUtc || newLocal);

        const origCombined = origUtc && origLocal
          ? `${formatSimpleTimestamp(origUtc)} / ${formatSimpleTimestamp(origLocal)}`
          : formatSimpleTimestamp(origUtc || origLocal);

        const latestEntry = [...values].sort(
          (a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0)
        )[values.length - 1];

        const ts = latestEntry?.timestamp || newUtcField?.[0]?.timestamp || newLocalField?.[0]?.timestamp || 
                   origUtcField?.[0]?.timestamp || origLocalField?.[0]?.timestamp || "";
        const evt = latestEntry?.eventType || "";

        pushRow({
          fieldCode: code,
          fieldType: "DATE",
          originalValue: origCombined,
          newValue: newCombined,
          timestamp: ts,
          eventType: evt,
        });
      }
    });

    rows.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
    return rows;
  };

  // NEW FUNCTION - handles statusUpdateHistory from new response format
  const buildStatusHistoryRows = (details) => {
    if (!details || !details.statusUpdateHistory || !Array.isArray(details.statusUpdateHistory)) {
      return [];
    }

    const rows = [];
    let eventCounter = 1;

    details.statusUpdateHistory.forEach((update, updateIndex) => {
      const timestamp = update.timestamp || "";
      const eventType = update.eventType || "";
      const source = update.source || "";
      const changes = update.changes || {};

      Object.entries(changes).forEach(([fieldPath, changeData]) => {
        const fieldCode = changeData.field || fieldPath;
        const from = changeData.from;
        const to = changeData.to;

        // Determine if this is a TEXT or DATE field
        let fieldType = "TEXT";
        let fieldDescription = fieldCode;
        
        if (UPDATED_DATE_FIELD_MAP[fieldCode]) {
          fieldType = "DATE";
          fieldDescription = UPDATED_DATE_FIELD_MAP[fieldCode];
        } else if (UPDATED_TEXT_FIELD_MAP[fieldCode]) {
          fieldType = "TEXT";
          fieldDescription = UPDATED_TEXT_FIELD_MAP[fieldCode];
        }

        // Format values and detect timezone for DATE fields
        const formatHistoryValue = (val) => {
          if (val === null || val === undefined) return { value: "-", timezone: null };
          
          const strVal = String(val);
          
          // For DATE fields, detect timezone
          if (fieldType === "DATE" && strVal.includes('T')) {
            const isUTC = strVal.includes('Z') || strVal.endsWith('UTC');
            const isIST = strVal.includes('+05:30') || strVal.includes('IST');
            
            let timezone = null;
            if (isUTC) timezone = 'UTC';
            else if (isIST) timezone = 'IST';
            
            return { value: strVal, timezone };
          }
          
          return { value: strVal, timezone: null };
        };

        const fromFormatted = formatHistoryValue(from);
        const toFormatted = formatHistoryValue(to);

        rows.push({
          key: `${updateIndex}-${fieldPath}-${eventCounter}`,
          eventNumber: eventCounter,
          fieldPath,
          fieldCode,
          fieldDescription,
          field: fieldCode,
          fieldType,
          from: fromFormatted.value,
          fromTimezone: fromFormatted.timezone,
          to: toFormatted.value,
          toTimezone: toFormatted.timezone,
          source,
          eventType,
          timestamp,
        });

        eventCounter++;
      });
    });

    // Sort by timestamp ascending (oldest to newest) for ALL filter
    rows.sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0));

    return rows;
  };

  const uniqueEventTypes = flightDetails ? getUniqueEventTypes(flightDetails) : [];
  const allRows = flightDetails ? buildTableRows(flightDetails) : [];
  const filteredRows = filterRowsByEventType(allRows, selectedEventType);
  const updatedRows = flightDetails ? buildUpdatedRows(flightDetails) : [];
  const statusHistoryRows = flightDetails ? buildStatusHistoryRows(flightDetails) : [];

  const getUpdatedUniqueEvents = (details, rows) => {
    if (!details || !details.eventHistory || !rows || rows.length === 0) {
      return [];
    }

    return details.eventHistory.map((event) => {
      const full = formatTimestamp(event.timestamp);
      const parts = full.split(" / ");
      const istPart = parts.length > 1 ? parts[1] : full;

      const count = rows.filter((row) => row.timestamp === event.timestamp).length;

      return {
        id: `${event.eventType}|${event.timestamp}`,
        label: `${event.eventType} (${istPart})`,
        eventType: event.eventType,
        timestamp: event.timestamp,
        count,
      };
    });
  };

  const getStatusHistoryUniqueEvents = (details, rows) => {
    if (!details || !details.statusUpdateHistory || !rows || rows.length === 0) {
      return [];
    }

    return details.statusUpdateHistory.map((update) => {
      const full = formatTimestamp(update.timestamp);
      const parts = full.split(" / ");
      const istPart = parts.length > 1 ? parts[1] : full;

      const count = rows.filter((row) => row.timestamp === update.timestamp).length;

      return {
        id: `${update.eventType}|${update.timestamp}`,
        label: `${update.eventType} (${istPart})`,
        eventType: update.eventType,
        timestamp: update.timestamp,
        source: update.source || "Unknown",
        count,
      };
    });
  };

  const updatedUniqueEvents = useMemo(
    () => getUpdatedUniqueEvents(flightDetails, updatedRows),
    [flightDetails, updatedRows]
  );

  const statusHistoryUniqueEvents = useMemo(
    () => getStatusHistoryUniqueEvents(flightDetails, statusHistoryRows),
    [flightDetails, statusHistoryRows]
  );

  const filterUpdatedRowsByEventId = (rows, eventTypeId) => {
    if (!rows || eventTypeId === "ALL") return rows;

    const targetTimestamp = eventTypeId.split("|")[1];
    if (!targetTimestamp) return rows;

    return rows.filter((row) => row.timestamp === targetTimestamp);
  };

  const filterStatusHistoryByEventId = (rows, eventTypeId) => {
    if (!rows || eventTypeId === "ALL") return rows;

    const targetTimestamp = eventTypeId.split("|")[1];
    if (!targetTimestamp) return rows;

    return rows.filter((row) => row.timestamp === targetTimestamp);
  };

  // Add this before JSX return (after other useMemos)
  const flightSummary = useMemo(() => buildFlightSummary(flightDetails), [flightDetails]);
  const summaryData = flightSummary[0] || {};


  const filteredUpdatedRows = useMemo(() => {
    if (!updatedRows || updatedRows.length === 0) return [];

    let rows = filterUpdatedRowsByEventId(updatedRows, updatedEventTypeFilter);

    if (updatedFieldTypeFilter && updatedFieldTypeFilter !== "ALL") {
      const typeFilter = String(updatedFieldTypeFilter).trim().toUpperCase();
      rows = rows.filter(
        (row) => String(row.fieldType || "").trim().toUpperCase() === typeFilter
      );
    }

    return rows;
  }, [updatedRows, updatedEventTypeFilter, updatedFieldTypeFilter]);

const filteredStatusHistoryRows = useMemo(() => {
  // Apply filters first (existing logic)
  let rows = filterStatusHistoryByEventId(statusHistoryRows, statusHistoryEventFilter);
  
  if (statusHistoryFieldTypeFilter && statusHistoryFieldTypeFilter !== 'ALL') {
    const typeFilter = String(statusHistoryFieldTypeFilter).trim().toUpperCase();
    rows = rows.filter(row => String(row.fieldType).trim().toUpperCase() === typeFilter);
  }

  // Include ALL events (don't skip duplicates)
  const allRows = rows.map(row => ({
    ...row,
    // Add change count info for display
    totalChanges: statusHistoryRows?.filter(r => r.fieldCode === row.fieldCode).length || 1,
    isHistoryRow: false
  }));

  // Insert history rows for expanded fields
  const finalRows = [];
  const fieldPositions = {}; // Track where each field first appears

  allRows.forEach((row, index) => {
    const fieldCode = row.fieldCode;
    
    // Track first position of each field
    if (!fieldPositions[fieldCode]) {
      fieldPositions[fieldCode] = index;
    }

    finalRows.push(row);
    
    // Add history row immediately after FIRST occurrence if expanded
    if (index === fieldPositions[fieldCode] && 
        row.totalChanges > 1 && 
        expandedFieldCodes[fieldCode]) {
      
      const fieldChanges = statusHistoryRows?.filter(r => r.fieldCode === fieldCode) || [];
      finalRows.push({
        key: `${row.key}-history`,
        isHistoryRow: true,
        fieldCode: `${fieldCode} History`,
        fieldDescription: buildHistoryText(fieldChanges),
        eventNumber: '',
        from: '',
        to: '',
        fieldType: '',
        source: '',
        eventType: '',
        timestamp: '',
        totalChanges: fieldChanges.length
      });
    }
  });

  return finalRows;
}, [statusHistoryRows, statusHistoryEventFilter, statusHistoryFieldTypeFilter, expandedFieldCodes]);

const categorizeField = (fieldCode, row) => {
  // Time fields by stage
  const scheduledTime = ['SGP', 'SGD', 'SGA', 'SQP', 'AQP'];
  const departureTime = ['STD', 'ETD', 'EGD', 'SRD', 'ERD'];
  const arrivalTime = ['BGG', 'AGA', 'ATA', 'ARA', 'AGT', 'ATM'];

  // STS status smart mapping
  if (fieldCode === 'STS') {
    const status = String(row.to || row.from || '').toUpperCase();
    
    if (status.includes('S') || status.includes('SCHED') || status.includes('PLANNED')) {
      return 'scheduled';
    }
    if (status.includes('G') || status.includes('GROUND') || status.includes('READY') || status.includes('BOARDING')) {
      return 'departure-preparation';
    }
    if (status.includes('A') || status.includes('AIRBOR') || status.includes('DEPARTED') || status.includes('OFFBLOCK') || status.includes('INFLIGHT')) {
      return 'en-route';
    }
    if (status.includes('L') || status.includes('LANDED') || status.includes('ARRIVED') || status.includes('ONBLOCK')) {
      return 'arrival';
    }
    return 'scheduled'; // safe default
  }

  // Time fields
  if (scheduledTime.includes(fieldCode)) return 'scheduled';
  if (departureTime.includes(fieldCode)) return 'departure-preparation';
  if (arrivalTime.includes(fieldCode)) return 'arrival';
  
  return 'scheduled'; // fallback
};


const lifecycleData = useMemo(() => {
  if (!flightDetails || !statusHistoryRows) return null;

  // Smart categorization that handles STS based on value, not just field code
  const categorizeField = (fieldCode, row) => {
    // Define field categories
    const scheduledFields = ['SGP', 'SGD', 'SGA', 'SQP', 'AQP'];
    const departureFields = ['STD', 'ETD', 'EGD', 'SRD', 'ERD', 'AGD'];
    const arrivalFields = ['BGG', 'AGA', 'ATA', 'ARA', 'AGT', 'ATM'];

    // Special handling for STS (flight status)
    if (fieldCode === 'STS') {
      const statusValue = String(row.to || row.from || '').toUpperCase();
      const eventType = String(row.eventType || '').toUpperCase();

      // Map by status value
      if (statusValue === 'S' || statusValue.includes('SCHED')) {
        return 'scheduled';
      }
      if (statusValue === 'A' || eventType === 'ENROUTE') {
        return 'en-route';
      }
      if (statusValue === 'L' || eventType === 'LANDED') {
        return 'arrival';
      }
      
      // Default for unknown STS
      return 'en-route';
    }

    // Regular field mapping
    if (scheduledFields.includes(fieldCode)) return 'scheduled';
    if (departureFields.includes(fieldCode)) return 'departure-preparation';
    if (arrivalFields.includes(fieldCode)) return 'arrival';
    
    return 'scheduled'; // default fallback
  };

  // Group rows by stage
  const rowsByStage = {};
  statusHistoryRows.forEach((row) => {
    const stage = categorizeField(row.fieldCode, row);
    if (!rowsByStage[stage]) {
      rowsByStage[stage] = [];
    }
    rowsByStage[stage].push(row);
  });

  // Stage configurations
  const stageConfigs = {
    scheduled: { 
      label: 'Scheduled', 
      color: '#1E90FF', 
      icon: <ClockCircleOutlined /> 
    },
    'departure-preparation': { 
      label: 'Departure Preparation', 
      color: '#FFC700', 
      icon: <SettingOutlined /> 
    },
    'en-route': { 
      label: 'En Route', 
      color: '#17D37A', 
      icon: <RocketOutlined /> 
    },
    arrival: { 
      label: 'Arrival', 
      color: '#7A5CFF', 
      icon: <CheckCircleOutlined /> 
    },
  };

  // Build stages with events
  const stages = Object.entries(stageConfigs).map(([key, config]) => {
    const events = (rowsByStage[key] || []).map((row) => ({
      code: row.fieldCode,
      label: row.fieldDescription,
      type: row.eventType || 'UPDATE',
      toText: `${row.from || '-'} → ${row.to || '-'} — ${formatSimpleTimestamp(row.timestamp)}`,
    }));

    const count = events.length;
    
    return {
      key,
      ...config,
      chips: count > 0 
        ? [`${count} ${config.label.toLowerCase()} update${count > 1 ? 's' : ''}`] 
        : [],
      events,
    };
  });

  return {
    statusHistoryId: flightDetails?.alertRule?.ruleId || `${flightNumber}-${departureDate?.format('YYYY-MM-DD')}`,
    stages,
  };
}, [flightDetails, statusHistoryRows, flightNumber, departureDate]);





// const buildHistoryText = (fieldChanges) => {
//   if (!fieldChanges || fieldChanges.length <= 1) return '';
  
//   const sorted = fieldChanges.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
//   const historyParts = sorted.map((change, idx) => {
//     const ts = formatSimpleTimestamp(change.timestamp);
//     return idx === 0 
//       ? `${change.from || '-'} → ${change.to || '-'} [${ts}]`
//       : `${change.from || '-'} → ${change.to || '-'} [${ts}]`;
//   });
  
//   return historyParts.join(', ');
// };

  const mainTableColumns = [
    {
      title: 'Field',
      dataIndex: 'field',
      key: 'field',
      width: 200,
      fixed: 'left',
      render: (field, record) => (
        <div>
          <div>{field}</div>
          {record.fieldStatus && (
            <Tag 
              color={record.fieldStatus === 'new' ? 'green' : 'orange'} 
              style={{ marginTop: 4, fontSize: '10px' }}
            >
              {record.fieldStatus.toUpperCase()}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Change Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 260,
      render: (timestamp) => {
        if (!timestamp) return <div style={{ fontSize: '12px' }}>-</div>;
        
        const raw = String(timestamp);
        // Extract yyyy-mm-dd hh:mm:ss.ms part (handles +05:30 offset)
        const core = raw.slice(0, 23).replace('T', ' '); // 2026-01-02 15:39:24.03
        const istDisplay = `${core} IST`;
        
        return (
          <div style={{ fontSize: '12px', lineHeight: 1.4 }}>
            {istDisplay}
          </div>
        );
      },
    },






    // ... rest of columns

    {
      title: 'Event Type',
      dataIndex: 'eventType',
      key: 'eventType',
      width: 190,
      render: (eventType) => (
        <span className={`event-badge event-${eventType?.toLowerCase().replace("_", "-") || "default"}`}>
          {eventType || "-"}
        </span>
      ),
    },
    {
      title: 'Initial Value',
      dataIndex: 'initialValue',
      key: 'initialValue',
      width: 180,
      render: (value, record) => {
        const rawValue = String(value || '-');
        if (rawValue === '-') return <div style={{ fontSize: '12px', lineHeight: 1.4 }}>-</div>;
        
        const formattedValue = formatValue(value, record.field);
        const isUTC = rawValue.endsWith('.000Z');
        const isLocal = rawValue.endsWith('.000') && !rawValue.endsWith('.000Z');
        
        return (
          <div>
            <div style={{ fontSize: '12px', lineHeight: 1.4 }}>
              {formattedValue}
            </div>
            {isUTC && (
              <Tag 
                color="blue" 
                style={{ marginTop: 4, fontSize: '10px' }}
              >
                UTC
              </Tag>
            )}
            {isLocal && (
              <Tag 
                color="green" 
                style={{ marginTop: 4, fontSize: '10px' }}
              >
                LOCAL
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: 'Final Value',
      dataIndex: 'finalValue',
      key: 'finalValue',
      width: 180,
      render: (value, record) => {
        const rawValue = String(value || '-');
        if (rawValue === '-') return <div style={{ fontSize: '12px', lineHeight: 1.4 }}>-</div>;
        
        const formattedValue = formatValue(value, record.field);
        const isUTC = rawValue.endsWith('.000Z');
        const isLocal = rawValue.endsWith('.000') && !rawValue.endsWith('.000Z');
        
        return (
          <div>
            <div style={{ fontSize: '12px', lineHeight: 1.4 }}>
              {formattedValue}
            </div>
            {isUTC && (
              <Tag 
                color="blue" 
                style={{ marginTop: 4, fontSize: '10px' }}
              >
                UTC
              </Tag>
            )}
            {isLocal && (
              <Tag 
                color="green" 
                style={{ marginTop: 4, fontSize: '10px' }}
              >
                LOCAL
              </Tag>
            )}
          </div>
        );
      },
    },



    {
      title: 'Value History',
      dataIndex: 'valueHistory',
      key: 'valueHistory',
      render: (history, record) => (
        <div style={{ fontSize: '12px' }}>
          {history.map((val) => formatValue(val, record.field)).join(', ')}
        </div>
      ),
    },
  ];

  const changeTableColumns = [
    {
      title: 'Field',
      dataIndex: 'field',
      key: 'field',
      width: 200,
    },
    {
      title: 'Previous Value',
      dataIndex: 'previousValue',
      key: 'previousValue',
      width: 200,
      render: (value, record) => formatValue(value, record.fieldName),
    },
    {
      title: 'Current Value',
      dataIndex: 'currentValue',
      key: 'currentValue',
      width: 200,
      render: (value, record) => formatValue(value, record.fieldName),
    },
    {
      title: 'Last Change Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 250,
      render: (timestamp) => {
        const full = formatTimestamp(timestamp);
        const parts = full.split(' / ');
        const ist = parts.length > 1 ? parts[1] : full;
        return <div style={{ fontSize: '12px', lineHeight: '1.4' }}>{ist}</div>;
      },
    },
  ];

  const updatedTableColumns = [
    {
      title: 'Field Code',
      dataIndex: 'fieldCode',
      key: 'fieldCode',
      width: 120,
    },
    {
      title: 'Field Name',
      dataIndex: 'description',
      key: 'description',
      width: 260,
    },
    {
      title: 'Field Type',
      dataIndex: 'fieldType',
      key: 'fieldType',
      width: 100,
    },
    {
      title: 'Original Value',
      dataIndex: 'originalValue',
      key: 'originalValue',
      width: 260,
      render: (value) => <span>{formatToISTOnly(value)}</span>,
    },
    {
      title: 'New Value',
      dataIndex: 'newValue',
      key: 'newValue',
      width: 260,
      render: (value) => <span>{formatToISTOnly(value)}</span>,
    },
    {
      title: 'Event Type',
      dataIndex: 'eventType',
      key: 'eventType',
      width: 150,
      render: (value) => value || '-',
    },
    {
      title: 'Update Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 250,
      render: (timestamp) => {
        const full = formatTimestamp(timestamp);
        const parts = full.split(' / ');
        const ist = parts.length > 1 ? parts[1] : full;
        return <span>{ist}</span>;
      },
    },
  ];

  const statusHistoryTableColumns = [
    {
      title: 'Event #',
      dataIndex: 'eventNumber',
      key: 'eventNumber',
      width: 90,
      align: 'center',
    },
    {
      title: 'Field Code',
      dataIndex: 'fieldCode',
      key: 'fieldCode',
      width: 420,
      render: (fieldCode, record) => {
        if (record.isHistoryRow) {
          return (
            <div style={{
              paddingLeft: 32,  // 👈 Slightly more indent
              background: '#f8f9fa',  // 👈 Lighter background
              borderRadius: 6,
              padding: '12px 16px',  // 👈 More padding
              fontSize: 12,
              color: '#495057',
              borderLeft: '4px solid #1890ff',
              whiteSpace: 'pre-wrap',  // 👈 Allow wrapping
              wordBreak: 'break-word', // 👈 Break long words
              lineHeight: 1.4,
              maxWidth: '100%'  // 👈 Full width available
            }}>
              fieldCode === 'STS' && (
              <Tag color="blue" style={{ fontSize: 10 }}>
                STATUS
              </Tag>
            
              {record.fieldDescription}
            </div>
          );
        }


        // Main field row - check if expandable
        const fieldChanges = statusHistoryRows?.filter(r => r.fieldCode === fieldCode) || [];
        const hasMultipleChanges = fieldChanges.length > 1;
        const isExpanded = expandedFieldCodes[fieldCode];

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {hasMultipleChanges && (
              <DownOutlined 
                style={{ 
                  cursor: 'pointer', 
                  color: '#1890ff',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }} 
                onClick={() => toggleFieldExpand(fieldCode)} 
              />
            )}
            <div>
              <div style={{ fontWeight: 500 }}>{fieldCode}</div>
              <div style={{ fontSize: '11px', color: '#666', marginTop: 2 }}>
                {record.fieldDescription}
                {hasMultipleChanges && (
                  <span style={{ marginLeft: 8, color: '#722ed1' }}>
                    ({fieldChanges.length} changes)
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      },
    },

    {
      title: 'Field Type',
      dataIndex: 'fieldType',
      key: 'fieldType',
      width: 100,
    },
    {
      title: 'From',
      dataIndex: 'from',
      key: 'from',
      width: 220,
      render: (value, record) => (
        <div>
          <div>{value}</div>
          {record.fromTimezone && (
            <Tag 
              color={record.fromTimezone === 'UTC' ? 'blue' : 'green'} 
              style={{ marginTop: 4, fontSize: '10px' }}
            >
              {record.fromTimezone}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'To',
      dataIndex: 'to',
      key: 'to',
      width: 220,
      render: (value, record) => (
        <div>
          <div>{value}</div>
          {record.toTimezone && (
            <Tag 
              color={record.toTimezone === 'UTC' ? 'blue' : 'green'} 
              style={{ marginTop: 4, fontSize: '10px' }}
            >
              {record.toTimezone}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 120,
    },
    {
      title: 'Event Type',
      dataIndex: 'eventType',
      key: 'eventType',
      width: 150,
      render: (eventType) => (
        <span className={`event-badge event-${eventType?.toLowerCase().replace("_", "-") || "default"}`}>
          {eventType || "-"}
        </span>
      ),
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 250,
      render: (timestamp) => {
        const full = formatTimestamp(timestamp);
        const parts = full.split(' / ');
        const ist = parts.length > 1 ? parts[1] : full;
        return <span>{ist}</span>;
      },
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <Title level={1} style={{ textAlign: 'center', marginBottom: '32px' }}>
          Cirium Flight Service
        </Title>

        {/* Rules Management */}
        <Card
          title="Created Flight Rules"
          style={{ marginBottom: '24px' }}
          extra={
            <Button danger icon={<ClearOutlined />} onClick={clearAllRules}>
              Clear All
            </Button>
          }
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Title level={5}>Upcoming Flights</Title>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {rulesCreated.upcoming?.flights?.length ? (
                  rulesCreated.upcoming.flights.map((rule, index) => (
                    <Tag
                      key={index}
                      color="blue"
                      closable
                      onClose={() => deleteRule(index)}
                      style={{ cursor: 'pointer', padding: '4px 8px', marginBottom: '8px' }}
                      onClick={() => populateFromRule(rule)}
                    >
                      {rule}
                    </Tag>
                  ))
                ) : (
                  <Text type="secondary">No upcoming rules</Text>
                )}
              </div>
            </Col>
            <Col xs={24} md={12}>
              <Title level={5}>History</Title>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {rulesCreated.history?.flights && rulesCreated.history.flights.length > 0 ? (
                  <>
                    {(
                      [...rulesCreated.history.flights].sort((a, b) => {
                        // a, b look like "DEL-AI-2469-2025-11-11"
                        const getDate = (id) => {
                          const parts = String(id || '').split('-');
                          const y = parts[3], m = parts[4], d = parts[5];
                          if (!y || !m || !d) return 0;
                          return new Date(`${y}-${m}-${d}`).getTime();
                        };
                        // Newest date first
                        return getDate(b) - getDate(a);
                      })
                    )
                      .slice(0, showAllHistory ? undefined : 9)
                      .map((rule, index) => (
                        <Tag
                          key={`history-${index}`}
                          color="default"
                          style={{ cursor: 'pointer' }}
                          onClick={() => populateFromRule(rule)}
                        >
                          {rule}
                        </Tag>
                      ))}

                    {rulesCreated.history.flights.length > 9 && (
                      <Tag
                        color="purple"
                        style={{ cursor: 'pointer', fontWeight: 500 }}
                        onClick={() => setShowAllHistory(!showAllHistory)}
                      >
                        {showAllHistory
                          ? 'See less'
                          : `+${rulesCreated.history.flights.length - 9} See more`}
                      </Tag>
                    )}
                  </>
                ) : (
                  <Text type="secondary">No history rules</Text>
                )}
              </div>
            </Col>
          </Row>
        </Card>

        {/* Flight Input Form */}
        <Card title="Flight Information" style={{ marginBottom: '24px' }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleGetFlightDetails}
            initialValues={{ departureDate: dayjs() }}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  label="Departure Airport Code"
                  name="departureAirportCode"
                  rules={[{ required: true, message: 'Please input departure airport code!' }]}
                >
                  <Input
                    placeholder="e.g., DEL"
                    value={departureAirportCode}
                    onChange={(e) => setDepartureAirportCode(e.target.value.toUpperCase())}
                    style={{ textTransform: 'uppercase' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  label="Carrier Code"
                  name="carrierCode"
                  rules={[{ required: true, message: 'Please input carrier code!' }]}
                >
                  <Input
                    placeholder="e.g., AI"
                    value={carrierCode}
                    onChange={(e) => setCarrierCode(e.target.value.toUpperCase())}
                    style={{ textTransform: 'uppercase' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  label="Flight Number"
                  name="flightNumber"
                  rules={[{ required: true, message: 'Please input flight number!' }]}
                >
                  <Input placeholder="e.g., 101" value={flightNumber} onChange={(e) => setFlightNumber(e.target.value)} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  label="Departure Date"
                  name="departureDate"
                  rules={[{ required: true, message: 'Please select departure date!' }]}
                >
                  <DatePicker
                    format="DD-MM-YYYY"
                    style={{ width: '100%' }}
                    value={departureDate}
                    onChange={(date) => setDepartureDate(date)}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Space>
              <Button type="primary" htmlType="submit" loading={loadingDetails} icon={<SearchOutlined />} size="large">
                {loadingDetails ? "Loading..." : "Get Flight Details"}
              </Button>
              <Button type="default" onClick={handleCreateFlightRule} loading={loadingRule} icon={<ReloadOutlined />} size="large">
                {loadingRule ? "Loading..." : "Create Flight Rule"}
              </Button>
            </Space>
          </Form>
        </Card>

        {/* Flight Details */}
        {flightDetails && (
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={3} style={{ margin: 0 }}>Flight Details</Title>
                <Space>
                  <Button icon={<DownloadOutlined />} onClick={downloadJSON}>Download JSON</Button>
                  <Button icon={<DownloadOutlined />} onClick={downloadCSV}>Download CSV</Button>
                </Space>
              </div>
            }
            style={{ marginBottom: '24px' }}
          >
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col xs={24} md={12}>
                <Space align="center" style={{ display: 'flex', flexWrap: 'nowrap' }}>
                  <FilterOutlined />
                  <Text strong style={{ whiteSpace: 'nowrap' }}>Event Filter:</Text>
                  <Select value={selectedEventType} onChange={setSelectedEventType} style={{ width: 600 }}>
                    <Option value="ALL">All Events ({allRows.length})</Option>
                    {uniqueEventTypes.map((event) => {
                      const count = allRows.filter((row) => row.timestamp === event.timestamp).length;
                      return (
                        <Option key={event.id} value={event.id}>
                          {event.label} - {event.dataSource} ({count})
                        </Option>
                      );
                    })}
                  </Select>
                </Space>
              </Col>
              {/* <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                <Space>
                  <ClockCircleOutlined />
                  <Text strong>Time Filter:</Text>
                  <Select value={timeFilter} onChange={setTimeFilter} style={{ width: 150 }}>
                    <Option value="ALL">All</Option>
                    <Option value="UTC">UTC</Option>
                    <Option value="LOCAL">Local</Option>
                  </Select>
                </Space>
              </Col> */}
            </Row>

            {selectedEventType !== "ALL" && (
              <Alert
                message={`Showing ${filteredRows.length} entries for event: ${
                  uniqueEventTypes.find((e) => e.id === selectedEventType)?.label
                }`}
                type="info"
                style={{ marginBottom: '16px' }}
              />
            )}

            <Spin spinning={loadingDetails}>
              <Table
                columns={mainTableColumns}
                dataSource={filteredRows}
                scroll={{ x: 1200, y: 400 }}
                pagination={{
                  pageSize: 50,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `Total ${total} items`,
                }}
                rowClassName={(record) => record.hasMismatch ? 'mismatch-row' : ''}
              />
            </Spin>
          </Card>
        )}

        {flightDetails && (
          <Card title="Flight Summary" style={{ marginBottom: 24 }}>
            <Row gutter={16}>
              <Col span={12}>
                <div style={{ padding: 16, border: '1px solid #d9d9d9', borderRadius: 8 }}>
                  <h4><strong>{summaryData.origin || '-'}</strong> → <strong>{summaryData.dest || '-'}</strong></h4>
                  <div><strong>Origin Terminal:</strong> T{summaryData.originTerminal}</div>
                  <div><strong>Dest Terminal:</strong> T{summaryData.destTerminal}</div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ padding: 16, border: '1px solid #d9d9d9', borderRadius: 8 }}>
                  <h4>Status</h4>
                  <Tag
                    color={
                      summaryData.status === 'L' ? 'green' :
                      summaryData.status === 'A' ? 'blue' :
                      summaryData.status === 'S' ? 'orange' : 'default'
                    }
                  >
                    {summaryData.status === 'S' ? 'Scheduled' :
                    summaryData.status === 'A' ? 'Airborne' :
                    summaryData.status === 'L' ? 'Landed' :
                    summaryData.status || 'Unknown'}
                  </Tag>

                  <div style={{ marginTop: 8 }}>
                    <strong>A/C:</strong> {summaryData.tailNumber} ({summaryData.aircraft})
                  </div>
                  <div><strong>Baggage:</strong> {summaryData.baggage}</div>
                </div>
              </Col>
            </Row>
            <Divider />
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <h5>Departure</h5>
                <div><strong>STD:</strong> {formatSimpleTimestamp(summaryData.std)}</div>
                <div><strong>ETD:</strong> {formatSimpleTimestamp(summaryData.etd)}</div>
                <div style={{ color: summaryData.atd ? '#52c41a' : '#999' }}>
                  <strong>ATD:</strong> {formatSimpleTimestamp(summaryData.atd) || '-'}
                </div>
              </Col>
              <Col span={12}>
                <h5>Arrival</h5>
                <div><strong>STA:</strong> {formatSimpleTimestamp(summaryData.sta)}</div>
                <div><strong>ETA:</strong> {formatSimpleTimestamp(summaryData.eta)}</div>
                <div style={{ color: summaryData.ata ? '#52c41a' : '#999' }}>
                  <strong>ATA:</strong> {formatSimpleTimestamp(summaryData.ata) || '-'}
                </div>
              </Col>
            </Row>
          </Card>
        )}

        {/* Change Summary Table */}
        {/* {flightDetails && (
          <Card
            title="Change Summary"
            style={{
              marginBottom: '24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
            bodyStyle={{ background: 'white' }}
            headStyle={{
              background: 'rgba(255,255,255,0.1)',
              color: 'white',
              border: 'none'
            }}
          >
            {changedRows.length === 0 ? (
              <Alert message="No changed fields detected since last fetch." type="info" showIcon />
            ) : (
              <Table
                columns={changeTableColumns}
                dataSource={changedRows.map((item, index) => ({ ...item, key: index }))}
                pagination={false}
                scroll={{ x: 800 }}
              />
            )}
          </Card>
        )} */}

        {/* Updated Fields (Old Format) */}
        {flightDetails && updatedRows.length > 0 && (
          <Card
            title="Updated Fields (Legacy Format)"
            style={{ marginBottom: '24px' }}
            extra={
              <Button icon={<DownloadOutlined />} onClick={downloadUpdatedCSV} disabled={!updatedRows || updatedRows.length === 0}>
                Download CSV
              </Button>
            }
          >
            <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
              <FilterOutlined />
              <Text strong>Field Type:</Text>
              <Select value={updatedFieldTypeFilter} onChange={setUpdatedFieldTypeFilter} style={{ width: 150 }}>
                <Option value="ALL">ALL</Option>
                <Option value="TEXT">TEXT</Option>
                <Option value="DATE">DATE</Option>
              </Select>

              <Text strong style={{ marginLeft: 16 }}>Event Type:</Text>
              <Select value={updatedEventTypeFilter} onChange={setUpdatedEventTypeFilter} style={{ width: 480 }} dropdownStyle={{ maxHeight: 400 }}>
                <Option value="ALL">All Events ({updatedRows?.length || 0})</Option>
                {updatedUniqueEvents.map((event) => (
                  <Option key={event.id} value={event.id}>
                    {`${event.label} – ${event.count}`}
                  </Option>
                ))}
              </Select>
            </Space>

            <Table
              columns={updatedTableColumns}
              dataSource={filteredUpdatedRows}
              pagination={false}
              scroll={{ x: 1100 }}
            />
          </Card>
        )}

        {/* Status Update History (New Format) */}
        {flightDetails && statusHistoryRows.length > 0 && (
          <Card
            title="Status Update History"
            style={{ marginBottom: '24px' }}
            extra={
              <Button
                icon={<DownloadOutlined />}
                onClick={downloadStatusHistoryCSV}
                disabled={!statusHistoryRows || statusHistoryRows.length === 0}
              >
                Download CSV
              </Button>
            }
          >
            <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
              <FilterOutlined />
              <Text strong>Field Type:</Text>
              <Select
                value={statusHistoryFieldTypeFilter}
                onChange={setStatusHistoryFieldTypeFilter}
                style={{ width: 150 }}
              >
                <Option value="ALL">ALL</Option>
                <Option value="TEXT">TEXT</Option>
                <Option value="DATE">DATE</Option>
              </Select>

              <Text strong style={{ marginLeft: 16 }}>Event Filter:</Text>
              <Select
                value={statusHistoryEventFilter}
                onChange={setStatusHistoryEventFilter}
                style={{ width: 480 }}
                dropdownStyle={{ maxHeight: 400 }}
              >
                <Option value="ALL">All Events ({statusHistoryRows?.length || 0})</Option>
                {statusHistoryUniqueEvents.map((event) => (
                  <Option key={event.id} value={event.id}>
                    {`${event.label} – ${event.source} (${event.count})`}
                  </Option>
                ))}
              </Select>
            </Space>

            <Table 
              columns={statusHistoryTableColumns}
              dataSource={filteredStatusHistoryRows}
              rowClassName={(record, index) => {
                // Highlight STS rows
                if (record.fieldCode === 'STS' || record.fieldCode?.includes('STS')) {
                  return 'sts-highlight-row';
                }
                // Also highlight STS history rows
                if (record.isHistoryRow && record.fieldCode?.includes('STS History')) {
                  return 'sts-history-row';
                }
                return '';
              }}
              pagination={{ pageSize: 50, showSizeChanger: true, showQuickJumper: true, showTotal: total => `Total ${total} items` }}
              scroll={{ x: 1400 }}
            />

          </Card>
        )}

        {flightDetails && (
          <Card 
            title="Flight Lifecycle" 
            style={{ marginTop: 24 }}
            bodyStyle={{ padding: 0 }}
          >
            <FlightLifecycle data={lifecycleData} />
          </Card>
        )}


        

        {ruleResponse?.message && (
          <Alert message={ruleResponse.message} type={'error'} style={{ marginBottom: '24px' }} showIcon />
        )}
      </div>
    </div>
  );
};

export default CiriumService;